import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import RefreshIcon from "@mui/icons-material/Refresh";
import {Alert, AlertTitle, Autocomplete, Box, Chip, Divider, Stack} from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type {
    SimphonyResourceOption,
    SimphonyRevenueCenterTables
} from "crqlar-react-common/dist/models/integrations/simphony/simphonyTableMapping.dto";
import {SimphonyService} from "crqlar-react-common/dist/services/integrations/simphony/simphony.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";

interface Props {
    configId?: string
    /** Lets the page's own save button light up while this tab holds unsaved bindings. */
    onDirtyChange?: (dirty: boolean) => void
    /**
     * Hands the page a way to flush these bindings.
     *
     * The bindings are written onto floor-plan resources, not onto the connector document, so they
     * cannot ride along inside the config payload — but they must still save from the same button
     * as everything else on this screen.
     */
    registerSave?: (save: () => Promise<void>) => void
}

/** Local edits, keyed `rvcRef|tableName` so a table name can repeat across revenue centers. */
type Draft = { [key: string]: string | undefined }

const draftKey = (rvcRef?: string, tableName?: string) => `${rvcRef}|${tableName}`;

/** Keeps the POS column and the floor-plan column lined up down the whole list. */
const ROW_GRID = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'minmax(140px, 220px) 1fr'},
    gap: 2,
    alignItems: 'start'
} as const;

/**
 * Binds each Simphony table to a crqlar floor-plan table.
 *
 * ### Why this screen matters more than it looks
 *
 * A Simphony check carries `tableName` and nothing that identifies a person — the POS has no guest
 * concept at all. The table is therefore the only bridge from a settled check back to the guest who
 * ate the meal: check -> table -> floor plan -> who was seated there. An unbound table still
 * produces a folio, so no revenue is lost, but that spend can never reach a profile.
 *
 * ### Set up once
 *
 * Table layouts do not change week to week, so this is a setup step, not a running concern. There
 * is deliberately no periodic reconciliation: the screen reads the POS list on demand, and the
 * binding lives on the floor-plan resource, so renaming a table in crqlar cannot break it.
 *
 * ### One direction only
 *
 * Simphony is the master. Table configuration lives in EMC and STSG2 exposes no create, update or
 * delete for tables, so a floor-plan table with no POS counterpart is shown as a job for the
 * property rather than something crqlar pretends to fix.
 */
const SimphonyTableMapping = ({configId, onDirtyChange, registerSave}: Props) => {

    const {t} = useTranslation();
    const readOnly = useReadOnly();

    const [draft, setDraft] = useState<Draft>({});

    const tablesQuery = useQuery<SimphonyRevenueCenterTables[]>(
        ['simphonyTables', configId],
        () => SimphonyService.listTables(configId!),
        {enabled: !!configId}
    );

    const revenueCenters = tablesQuery.data || [];

    // The saver reads through refs: the page holds the callback across renders, and a stale closure
    // here would silently save an older draft than the one on screen.
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const dataRef = useRef(revenueCenters);
    dataRef.current = revenueCenters;

    const dirty = Object.keys(draft).length > 0;

    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    useEffect(() => {
        registerSave?.(async () => {
            if (Object.keys(draftRef.current).length === 0) return;

            // Only the rows actually touched. Sending the whole list would make every save a
            // rewrite of every binding, so one stale render — a list loaded before the bindings
            // existed, say — would unbind the lot in a single click.
            const bindings = dataRef.current.flatMap(rc =>
                (rc.tables || [])
                    .filter(table => draftKey(rc.rvcRef, table.tableName) in draftRef.current)
                    .map(table => ({
                        rvcRef: rc.rvcRef,
                        tableName: table.tableName,
                        resourceId: draftRef.current[draftKey(rc.rvcRef, table.tableName)] || undefined
                    }))
            );

            if (bindings.length === 0) return;

            await SimphonyService.saveTables({id: configId!, bindings});
            setDraft({});
            await tablesQuery.refetch();
        });
    }, [configId, registerSave, tablesQuery]);

    const boundResourceId = (rvcRef?: string, tableName?: string, current?: string) => {
        const key = draftKey(rvcRef, tableName);
        return key in draft ? draft[key] : current;
    };

    const setBinding = (rvcRef?: string, tableName?: string, resourceId?: string) => {
        setDraft(prev => ({...prev, [draftKey(rvcRef, tableName)]: resourceId}));
    };

    const applySuggestions = () => {
        const next: Draft = {...draft};
        revenueCenters.forEach(rc => {
            rc.tables?.forEach(table => {
                const key = draftKey(rc.rvcRef, table.tableName);
                const current = key in next ? next[key] : table.resourceId;
                if (!current && table.suggestedResourceId) next[key] = table.suggestedResourceId;
            });
        });
        setDraft(next);
    };

    const suggestionCount = revenueCenters.reduce((total, rc) => total + (rc.tables || []).filter(table =>
        !boundResourceId(rc.rvcRef, table.tableName, table.resourceId) && !!table.suggestedResourceId
    ).length, 0);

    const unboundCount = revenueCenters.reduce((total, rc) => total + (rc.tables || []).filter(table =>
        !boundResourceId(rc.rvcRef, table.tableName, table.resourceId)
    ).length, 0);

    const optionLabel = (option: SimphonyResourceOption) => option.name || option.id || '';

    if (!configId) {
        return (
            <Alert severity={'info'}>
                {t('Save the connector first. Table bindings are written onto floor-plan tables, which needs a saved configuration.')}
            </Alert>
        );
    }

    return (
        <Stack gap={3}>
            <Stack gap={1}>
                <Typography variant={'h5'} textAlign={'start'}>{t('Tables')}</Typography>
                <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                    {t('A Simphony check names its table and nothing else — the POS has no guest records. Binding each POS table to a table in the floor plan is what lets a settled check reach the guest who was sitting there.')}
                </Typography>
                <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                    {t('Tables come from Simphony and are configured in EMC, so this is a one-time setup. A table with no binding still records its revenue — only the guest link is missing.')}
                </Typography>
            </Stack>

            <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
                <Button
                    variant={'outlined'}
                    startIcon={tablesQuery.isFetching ? <CircularProgress size={16}/> : <RefreshIcon/>}
                    disabled={tablesQuery.isFetching}
                    onClick={() => tablesQuery.refetch()}
                >
                    {t('Load tables from Simphony')}
                </Button>
                <Button
                    variant={'outlined'}
                    startIcon={<AutoFixHighIcon/>}
                    disabled={readOnly || suggestionCount === 0}
                    onClick={applySuggestions}
                >
                    {t('Apply {{count}} name matches', {count: suggestionCount})}
                </Button>
            </Stack>

            {tablesQuery.isError && (
                <Alert severity={'error'}>
                    {t('Could not read the tables from Simphony. Check the credentials and the chosen location.')}
                </Alert>
            )}

            {!tablesQuery.isLoading && revenueCenters.length === 0 && (
                // The same failure mode the revenue-centre map has: nothing here looks identical to
                // a property with no tables, so say which one it is.
                <Alert severity={'warning'}>
                    <AlertTitle>{t('No revenue centers mapped')}</AlertTitle>
                    {t('Map at least one revenue center to a sector on the Configuration tab first. Tables are read per revenue center, and only mapped ones are synced.')}
                </Alert>
            )}

            {revenueCenters.map(rc => (
                <Stack key={rc.rvcRef} gap={1.5}>
                    <Divider/>
                    <Stack direction={'row'} gap={1} alignItems={'center'} flexWrap={'wrap'}>
                        <Typography variant={'subtitle1'}>
                            {rc.rvcName ? `${rc.rvcRef} — ${rc.rvcName}` : rc.rvcRef}
                        </Typography>
                        <Chip
                            size={'small'}
                            variant={'outlined'}
                            label={t('{{count}} tables in the POS', {count: rc.tables?.length || 0})}
                        />
                    </Stack>

                    {(rc.tables || []).length === 0 && (
                        <Alert severity={'info'}>
                            {t('Simphony reports no tables for this revenue center. Counter-service outlets legitimately have none; otherwise the tables are configured in EMC.')}
                        </Alert>
                    )}

                    {(rc.tables || []).length > 0 && (
                        <Box sx={{...ROW_GRID, mb: -0.5}}>
                            <Typography variant={'caption'} color={'text.secondary'} textAlign={'start'}>
                                {t('Simphony table')}
                            </Typography>
                            <Typography variant={'caption'} color={'text.secondary'} textAlign={'start'}>
                                {t('crqlar floor-plan table')}
                            </Typography>
                        </Box>
                    )}

                    {(rc.tables || []).map(table => {
                        const value = boundResourceId(rc.rvcRef, table.tableName, table.resourceId);
                        const selected = (rc.sectorResources || []).find(option => option.id === value) || null;

                        return (
                            <Box key={draftKey(rc.rvcRef, table.tableName)} sx={ROW_GRID}>
                                <TextField
                                    value={table.tableName || ''}
                                    size={'small'}
                                    fullWidth
                                    InputProps={{readOnly: true}}
                                />
                                <Autocomplete
                                    fullWidth
                                    size={'small'}
                                    disabled={readOnly}
                                    options={rc.sectorResources || []}
                                    value={selected}
                                    getOptionLabel={optionLabel}
                                    isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
                                    onChange={(e, option) => setBinding(rc.rvcRef, table.tableName, option?.id)}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            <Stack direction={'row'} gap={1} alignItems={'center'}>
                                                <span>{optionLabel(option)}</span>
                                                {option.boundToTableName && option.boundToTableName !== table.tableName && (
                                                    <Chip
                                                        size={'small'}
                                                        variant={'outlined'}
                                                        color={'warning'}
                                                        label={t('already table {{table}}', {table: option.boundToTableName})}
                                                    />
                                                )}
                                            </Stack>
                                        </li>
                                    )}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            placeholder={t('Not bound')}
                                            helperText={
                                                !value && table.suggestedResourceName
                                                    ? t('Suggested: {{name}}', {name: table.suggestedResourceName})
                                                    : undefined
                                            }
                                        />
                                    )}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            ))}

            {revenueCenters.length > 0 && unboundCount > 0 && (
                <Alert severity={'warning'}>
                    {t('{{count}} POS tables are not bound. Checks rung on them are still recorded, but cannot be matched to a guest.', {count: unboundCount})}
                </Alert>
            )}
        </Stack>
    );
};

export default SimphonyTableMapping;
